import React from "react";
import { Row, Col, Card, Statistic, Tag, Progress } from "antd";
import {
  UserOutlined,
  WalletOutlined,
  SwapOutlined,
  WarningOutlined,
  FileTextOutlined,
  CarOutlined,
  ClockCircleOutlined,
  BellOutlined,
  DollarOutlined,
  BankOutlined,
} from "@ant-design/icons";

const AdminDashboardStats = ({ data }) => {
  if (!data) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Users & Wallets */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card className="hover:shadow-xl transition-all duration-300 border-l-4 border-green-500">
            <Statistic
              title={<span className="text-gray-600 font-medium">Utilisateurs Totaux</span>}
              value={data.users.total}
              prefix={<UserOutlined className="text-green-600" />}
              valueStyle={{ color: "#3f8600", fontSize: "28px", fontWeight: "bold" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="hover:shadow-xl transition-all duration-300 border-l-4 border-blue-500">
            <Statistic
              title={<span className="text-gray-600 font-medium">Portefeuilles Totaux</span>}
              value={data.wallets.total}
              prefix={<WalletOutlined className="text-blue-600" />}
              valueStyle={{ color: "#1890ff", fontSize: "28px", fontWeight: "bold" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="hover:shadow-xl transition-all duration-300 border-l-4 border-emerald-500">
            <Statistic
              title={<span className="text-gray-600 font-medium">Solde Portefeuille</span>}
              value={formatCurrency(data.wallets.balanceTotal)}
              prefix={<DollarOutlined className="text-emerald-600" />}
              valueStyle={{ color: "#10b981", fontSize: "24px", fontWeight: "bold" }}
            />
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Verrouillé:</span>
                <span className="font-semibold">{formatCurrency(data.wallets.lockedTotal)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Disponible:</span>
                <span className="font-semibold text-green-600">{formatCurrency(data.wallets.availableTotal)}</span>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="hover:shadow-xl transition-all duration-300 border-l-4 border-purple-500">
            <Statistic
              title={<span className="text-gray-600 font-medium">Transactions (24h)</span>}
              value={data.transactions.last24h}
              prefix={<SwapOutlined className="text-purple-600" />}
              valueStyle={{ color: "#722ed1", fontSize: "28px", fontWeight: "bold" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Revenus */}
      <Card
        title={
          <span className="text-lg font-semibold">
            <DollarOutlined className="mr-2 text-green-600" /> 
            Résumé des Revenus
          </span>
        }
        className="hover:shadow-xl transition-all duration-300 border-t-4 border-green-500 mb-6"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Card type="inner" className="bg-green-50/50">
              <Statistic
                title={<span className="text-gray-600 font-medium">Revenus tickets (Mois)</span>}
                value={data.revenue?.parkingMonth || 0}
                formatter={formatCurrency}
                valueStyle={{ color: "#3f8600", fontSize: "24px" }}
              />
              <div className="text-xs text-gray-500 mt-2 border-t pt-2">
                Aujourd'hui: <span className="font-semibold text-green-600">{formatCurrency(data.revenue?.parkingDay || 0)}</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card type="inner" className="bg-red-50/50">
              <Statistic
                title={<span className="text-gray-600 font-medium">Revenus Infractions (Mois)</span>}
                value={data.revenue?.fineMonth || 0}
                formatter={formatCurrency}
                valueStyle={{ color: "#cf1322", fontSize: "24px" }}
              />
              <div className="text-xs text-gray-500 mt-2 border-t pt-2">
                Aujourd'hui: <span className="font-semibold text-red-600">{formatCurrency(data.revenue?.fineDay || 0)}</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card type="inner" className="bg-purple-50/50">
              <Statistic
                title={<span className="text-gray-600 font-medium">Revenus Abonnements (Mois)</span>}
                value={data.revenue?.subscriptionMonth || 0}
                formatter={formatCurrency}
                valueStyle={{ color: "#722ed1", fontSize: "24px" }}
              />
              <div className="text-xs text-gray-500 mt-2 border-t pt-2">
                Aujourd'hui: <span className="font-semibold text-purple-600">{formatCurrency(data.revenue?.subscriptionDay || 0)}</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card type="inner" className="bg-blue-50/50">
              <Statistic
                title={<span className="text-gray-600 font-medium">Revenus Totaux (Mois)</span>}
                value={data.revenue?.totalMonth || 0}
                formatter={formatCurrency}
                valueStyle={{ color: "#1890ff", fontSize: "24px", fontWeight: "bold" }}
              />
              <div className="text-xs text-gray-500 mt-2 border-t pt-2">
                Total Aujourd'hui: <span className="font-semibold text-blue-600">{formatCurrency(data.revenue?.totalDay || 0)}</span>
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Transactions Details */}
      <Card 
        title={
          <span className="text-lg font-semibold">
            <SwapOutlined className="mr-2 text-purple-600" /> 
            Aperçu des Transactions
          </span>
        } 
        className="hover:shadow-xl transition-all duration-300 border-t-4 border-purple-500"
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="Total"
              value={data.transactions.total}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="En Attente"
              value={data.transactions.pending}
              valueStyle={{ color: "#faad14" }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Complété"
              value={data.transactions.completed}
              valueStyle={{ color: "#52c41a" }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Échoué"
              value={data.transactions.failed}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Col>
        </Row>
        <div className="mt-4">
          <div className="text-sm text-gray-500 mb-2">Taux de Réussite</div>
          <Progress
            percent={
              data.transactions.total > 0
                ? ((data.transactions.completed / data.transactions.total) * 100).toFixed(2)
                : 0
            }
            status={
              (data.transactions.completed / data.transactions.total) * 100 > 80
                ? "success"
                : "normal"
            }
          />
        </div>
      </Card>

      {/* Infractions & Disputes */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card 
            title={
              <span className="text-lg font-semibold">
                <WarningOutlined className="mr-2 text-red-600" /> 
                Infractions
              </span>
            } 
            className="hover:shadow-xl transition-all duration-300 border-t-4 border-red-500 h-full"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title={<span className="text-gray-600">Infractions Totales</span>}
                  value={data.infractions.total}
                  valueStyle={{ color: "#ff4d4f", fontSize: "24px", fontWeight: "bold" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title={<span className="text-gray-600">Non Notifié</span>}
                  value={data.infractions.unnotified}
                  valueStyle={{ color: "#faad14", fontSize: "24px", fontWeight: "bold" }}
                  suffix={data.infractions.unnotified > 0 ? <Tag color="warning">Action</Tag> : null}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card 
            title={
              <span className="text-lg font-semibold">
                <FileTextOutlined className="mr-2 text-blue-600" /> 
                Litiges
              </span>
            } 
            className="hover:shadow-xl transition-all duration-300 border-t-4 border-blue-500 h-full"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Litiges Totaux"
                  value={data.infractionDisputes.total}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="En Révision"
                  value={data.infractionDisputes.pending}
                  valueStyle={{ color: "#faad14" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Subscription Requests */}
      <Card 
        title={
          <span className="text-lg font-semibold">
            <FileTextOutlined className="mr-2 text-indigo-600" /> 
            Demandes d'Abonnement
          </span>
        } 
        className="hover:shadow-xl transition-all duration-300 border-t-4 border-indigo-500"
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8}>
            <Statistic
              title="Demandes Totales"
              value={data.subscriptionRequests.total}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Dernières 24h"
              value={data.subscriptionRequests.last24h}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <div className="text-sm font-medium text-gray-700 mb-2">Par Statut</div>
            <div className="space-y-1">
              {Object.entries(data.subscriptionRequests.byStatus || {}).map(([status, count]) => (
                <div key={status} className="flex justify-between text-xs">
                  <Tag color={
                    status === 'approved' ? 'green' :
                    status === 'pending' ? 'orange' :
                    status === 'rejected' ? 'red' : 'blue'
                  }>
                    {status}
                  </Tag>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Card>

      {/* Parking & Vehicles */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card 
            title={
              <span className="text-lg font-semibold">
                <BankOutlined className="mr-2 text-cyan-600" /> 
                Parking Infrastructure
              </span>
            } 
            className="hover:shadow-xl transition-all duration-300 border-t-4 border-cyan-500 h-full"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Parkings Totaux"
                  value={data.parking.parkingsTotal}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Actifs"
                  value={data.parking.parkingsActive}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Lignes"
                  value={data.parking.linesTotal}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card 
            title={
              <span className="text-lg font-semibold">
                <CarOutlined className="mr-2 text-amber-600" /> 
                Véhicules
              </span>
            } 
            className="hover:shadow-xl transition-all duration-300 border-t-4 border-amber-500 h-full"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Véhicules Totaux"
                  value={data.vehicles.total}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Masqués"
                  value={data.vehicles.hidden}
                  valueStyle={{ color: "#ff4d4f" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Parking Tickets */}
      <Card 
        title={
          <span className="text-lg font-semibold">
            <ClockCircleOutlined className="mr-2 text-teal-600" /> 
            Parking Tickets
          </span>
        } 
        className="hover:shadow-xl transition-all duration-300 border-t-4 border-teal-500"
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6} md={4}>
            <Statistic
              title="Total"
              value={data.parkingTickets.total}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic
              title="En Attente"
              value={data.parkingTickets.pending}
              valueStyle={{ color: "#faad14" }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic
              title="Actifs"
              value={data.parkingTickets.active}
              valueStyle={{ color: "#52c41a" }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic
              title="Expirés"
              value={data.parkingTickets.expired}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic
              title="Dernières 24h"
              value={data.parkingTickets.last24h}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Col>
        </Row>
      </Card>

      {/* Payment Systems */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card 
            title={
              <span className="text-lg font-semibold">
                <DollarOutlined className="mr-2 text-blue-600" /> 
                CMI Payments
              </span>
            } 
            className="hover:shadow-xl transition-all duration-300 border-t-4 border-blue-500 h-full"
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Paiements Totaux"
                  value={data.payments.cmi.total}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Montant Total"
                  value={formatCurrency(data.payments.cmi.amountCompleted)}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="En Attente"
                  value={data.payments.cmi.pending}
                  valueStyle={{ color: "#faad14" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Complété"
                  value={data.payments.cmi.completed}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Échoué"
                  value={data.payments.cmi.failed}
                  valueStyle={{ color: "#ff4d4f" }}
                />
              </Col>
            </Row>
            <div className="mt-4">
              <div className="text-sm text-gray-500 mb-2">Taux de Réussite</div>
              <Progress
                percent={
                  data.payments.cmi.total > 0
                    ? ((data.payments.cmi.completed / data.payments.cmi.total) * 100).toFixed(2)
                    : 0
                }
                status={
                  (data.payments.cmi.completed / data.payments.cmi.total) * 100 > 80
                    ? "success"
                    : "normal"
                }
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card 
            title={
              <span className="text-lg font-semibold">
                <DollarOutlined className="mr-2 text-purple-600" /> 
                Fatourati Payments
              </span>
            } 
            className="hover:shadow-xl transition-all duration-300 border-t-4 border-purple-500 h-full"
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Paiements Totaux"
                  value={data.payments.fatourati.total}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Montant Total"
                  value={formatCurrency(data.payments.fatourati.amountPaid)}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="En Attente"
                  value={data.payments.fatourati.pending}
                  valueStyle={{ color: "#faad14" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Payé"
                  value={data.payments.fatourati.paid}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
            </Row>
            <div className="mt-4">
              <div className="text-sm text-gray-500 mb-2">Taux de Réussite</div>
              <Progress
                percent={
                  data.payments.fatourati.total > 0
                    ? ((data.payments.fatourati.paid / data.payments.fatourati.total) * 100).toFixed(2)
                    : 0
                }
                status={
                  (data.payments.fatourati.paid / data.payments.fatourati.total) * 100 > 80
                    ? "success"
                    : "normal"
                }
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboardStats;
